import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Select } from '../../components/ui/FormField.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useOrganisationRoleNames } from '../../hooks/useRoleOptions.js';
import { ApiError, api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface Membership {
  _id: string;
  supplier_id: string;
  supplier_name: string;
  role: string;
  created_at: string;
  created_by_user_id: string;
}

interface MembershipsResponse {
  items: Membership[];
  active_supplier_id: string | null;
}

interface SuppliersResponse {
  items: { _id: string; company_name: string }[];
}

/** Admin panel to view a user's supplier memberships and add the user to a
 * supplier. Renders nothing unless the current admin can manage:'User'. */
export function SupplierMemberships({ userId }: { userId: string }) {
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');
  const queryClient = useQueryClient();
  const [supplierId, setSupplierId] = useState('');
  // 367 (design 1g): the organisation roles on the Roles page; Host unless another is chosen.
  const organisationRoles = useOrganisationRoleNames();
  const [chosenRole, setRole] = useState<string>('');
  const role = chosenRole || organisationRoles[0] || 'Host';
  const [error, setError] = useState('');

  const { data } = useQuery({
    queryKey: ['user-memberships', userId],
    queryFn: () => api.get<MembershipsResponse>(`/users/${userId}/memberships`),
    enabled: canManage,
  });

  const { data: suppliers } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<SuppliersResponse>('/suppliers'),
    enabled: canManage,
  });

  const add = useMutation({
    mutationFn: (body: { supplier_id: string; role: string }) =>
      api.post(`/users/${userId}/memberships`, body),
    onSuccess: () => {
      setError('');
      setSupplierId('');
      queryClient.invalidateQueries({ queryKey: ['user-memberships', userId] });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError && err.status === 409
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to add membership',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (supplierIdToRemove: string) =>
      api.delete(`/users/${userId}/memberships/${supplierIdToRemove}`),
    onSuccess: () => {
      setError('');
      queryClient.invalidateQueries({ queryKey: ['user-memberships', userId] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to remove membership');
    },
  });

  if (!canManage) return null;

  const memberships = data?.items ?? [];
  const supplierOptions = suppliers?.items ?? [];

  function onAdd() {
    if (supplierId === '') {
      setError('Select a supplier.');
      return;
    }
    add.mutate({ supplier_id: supplierId, role });
  }

  return (
    <Card>
      <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
        Supplier memberships
      </h2>

      {memberships.length === 0 ? (
        <p className="mb-4 text-sm text-steel">No supplier memberships.</p>
      ) : (
        <ul className="mb-4 grid gap-2">
          {memberships.map((m) => (
            <li
              key={m._id}
              className="flex items-center justify-between rounded-xl border border-hairline shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] px-3 py-2 text-sm"
            >
              <span className="text-ink">{m.supplier_name}</span>
              <span className="flex items-center gap-3">
                <span className="text-xs font-medium text-steel">{m.role}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={remove.isPending && remove.variables === m.supplier_id}
                  onClick={() => remove.mutate(m.supplier_id)}
                >
                  Remove
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <FormField label="Add to supplier" name="membership_supplier">
          <Select
            id="membership_supplier"
            name="membership_supplier"
            value={supplierId}
            onChange={(e) => setSupplierId((e.target as HTMLSelectElement).value)}
          >
            <option value="">Select supplier…</option>
            {supplierOptions.map((s) => (
              <option key={s._id} value={s._id}>
                {s.company_name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Role" name="membership_role">
          <Select
            id="membership_role"
            name="membership_role"
            value={role}
            onChange={(e) => setRole((e.target as HTMLSelectElement).value)}
          >
            {organisationRoles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </FormField>
        <Button size="sm" loading={add.isPending} onClick={onAdd}>
          Add
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </Card>
  );
}
